import React, { useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { dialogs, i18next, readOnly, utils, view, errors } from '@yola/ws-sdk';
import contextMenu from 'src/js/modules/context-menu';
import ContextMenuItem from 'src/js/modules/context-menu/components/context-menu-item';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import getParentBlockByElement from 'src/js/modules/blocks/helpers/get-parent-block-by-element';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import { IMAGE } from 'src/js/modules/bindings/constants/element-types';

const { STOCK_GALLERY_DIALOG, ADD_IMAGE_FROM_URL_DIALOG } = dialogTypes;

function UploadImageMenu({
  id,
  elementId = null,
  accept,
  onImageUpload,
  onStockPhotoUpload,
  onWrongFileType,
  onClick = utils.noop,
  onTriggerUploadImageClick = utils.noop,
  onTriggerBrowseStockPhotosClick = utils.noop,
  onUrlPhotoUpload = utils.noop,
  onUrlPhotoUploadCancel = utils.noop,
  enableAddFromUrl = false,
  onPhotoStockDialogCancel = utils.noop,
  className = '',
  children,
  calculatePositionDecorator = undefined,
  getContextMenuPosition = utils.noop,
  triggerNodeRef: triggerNodeRefProp = null,
  preserveSharedData = false,
  multiple = false,
  fileTriggerRef = null,
  menu,
  readOnlyStatus,
}) {
  const element = view.accessors.getLiveElement(elementId);
  const blockId = view.accessors.getLiveElementId(getParentBlockByElement(element));

  const [featureFlags] = useFeatureFlags(['yola_cms']);

  const triggerRef = useRef(null);
  const triggerNodeRef = triggerNodeRefProp || triggerRef;

  const groups = [
    {
      name: 'Upload image',
      element,
      items: [
        {
          id: 'upload-image',
          menuNode: (
            <ContextMenuItem
              caption={`${i18next.t('Upload a file')}...`}
              fileTriggerConfig={{
                id: 'upload-image',
                onChange: (fileList) => {
                  contextMenu.operations.hideContextMenu();
                  onImageUpload(fileList);
                },
                onWrongTypeFile: (...params) => {
                  contextMenu.operations.hideContextMenu();
                  onWrongFileType(...params);
                },
                accept,
                onClick: onTriggerUploadImageClick,
                forwardRef: fileTriggerRef,
                multiple,
              }}
            />
          ),
        },
        {
          id: 'browse-stock-images',
          caption: `${i18next.t('Browse free stock photos')}...`,
          onClick: () => {
            onTriggerBrowseStockPhotosClick();
            contextMenu.operations.hideContextMenu();
            dialogs.operations.show(STOCK_GALLERY_DIALOG, {
              multiple,
              onDialogMainAction: onStockPhotoUpload,
              onDialogCancel: onPhotoStockDialogCancel,
              preserveSharedData,
              blockId,
            });
          },
        },
        ...(featureFlags.yola_cms
          ? [
              {
                id: 'connect-to-binding',
                get caption() {
                  return i18next.t('Connect to CMS...');
                },
                onClick: () => {
                  contextMenu.operations.hideContextMenu();
                  dialogs.operations.show(dialogTypes.CONNECT_TO_CMS_DIALOG, {
                    elementId,
                    elementType: IMAGE,
                  });
                },
              },
            ]
          : []),
        ...(enableAddFromUrl
          ? [
              {
                id: 'add-from-url',
                caption: `${i18next.t('Add from URL')}...`,
                onClick: () => {
                  contextMenu.operations.hideContextMenu();
                  dialogs.operations.show(ADD_IMAGE_FROM_URL_DIALOG, {
                    preserveSharedData,
                    onDialogMainAction: onUrlPhotoUpload,
                    onDialogCancel: onUrlPhotoUploadCancel,
                  });
                },
              },
            ]
          : []),
      ],
    },
  ];

  const showContextMenu = () => {
    if (menu.triggerId === id) {
      contextMenu.operations.hideContextMenu();
      return;
    }

    if (readOnlyStatus) {
      errors.operations.catchError(null, errors.types.READ_ONLY_ERROR);
      return;
    }

    const position = getContextMenuPosition() || { x: 0, y: 0 };
    contextMenu.operations.showContextMenu({
      position,
      groups,
      triggerId: id,
      calculatePosition: calculatePositionDecorator,
    });
  };

  const shouldOpenContextMenu = (event) => !event?.target?.closest('.ws-ui-file-picker__clear');

  return (
    <div ref={triggerNodeRef} className={className} id={id}>
      {React.Children.map(children, (child) =>
        React.cloneElement(child, {
          onClick: (event) => {
            if (shouldOpenContextMenu(event)) {
              showContextMenu();
            }

            onClick(event);
          },
        })
      )}
    </div>
  );
}

UploadImageMenu.propTypes = {
  id: PropTypes.string.isRequired,
  elementId: PropTypes.string,
  className: PropTypes.string,
  onImageUpload: PropTypes.func.isRequired,
  onStockPhotoUpload: PropTypes.func.isRequired,
  onWrongFileType: PropTypes.func.isRequired,
  accept: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  onTriggerUploadImageClick: PropTypes.func,
  onTriggerBrowseStockPhotosClick: PropTypes.func,
  onUrlPhotoUpload: PropTypes.func,
  onUrlPhotoUploadCancel: PropTypes.func,
  enableAddFromUrl: PropTypes.bool,
  onPhotoStockDialogCancel: PropTypes.func,
  calculatePositionDecorator: PropTypes.func,
  getContextMenuPosition: PropTypes.func,
  triggerNodeRef: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  children: PropTypes.node.isRequired,
  preserveSharedData: PropTypes.bool,
  multiple: PropTypes.bool,
  fileTriggerRef: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  menu: PropTypes.shape({
    triggerId: PropTypes.string,
  }).isRequired,
  readOnlyStatus: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  menu: contextMenu.selectors.getMenu(state),
  readOnlyStatus: readOnly.selectors.getReadOnlyStatus(state),
});

export default connect(mapStateToProps)(UploadImageMenu);
